import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { X } from "lucide-react";
import { ApiClientError } from "@/api/client";
import {
  KbAdminSection,
  KbDuplicateExisting,
  KbListItem,
  fetchKbMovePreview,
  fetchKbSection,
  fetchKbSectionItemsAll,
  moveKbItem,
  newKbRequestId,
} from "@/api/knowledge";
import { KbDuplicateDialog } from "./KbDuplicateDialog";

/**
 * D-147 W1 S5 — «Перемістити матеріали…» (KbMoveService, §9): вибір
 * матеріалів → target розділ/папка → ПІДСУМКОВА зміна доступу ДО confirm
 * (мокап ⑥: «Побачать читачі „X", перестануть бачити читачі „Y"») →
 * послідовний move (кожен item = окрема TX з idempotency-requestId);
 * 409 duplicate_title → єдиний duplicate-діалог per-item (confirm-retry
 * той самий requestId). Pin скидається сервісом (AC-KB-22).
 */
export function KbMoveModal({
  source,
  candidates,
  onClose,
}: {
  source: KbAdminSection;
  /** target-кандидати: owner-grade розділи актора, активні, без source. */
  candidates: KbAdminSection[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [targetId, setTargetId] = useState("");
  const [targetFolderId, setTargetFolderId] = useState("");
  const [step, setStep] = useState<"pick" | "confirm" | "running" | "done">("pick");
  const [error, setError] = useState<string | null>(null);
  const [log, setLog] = useState<string[]>([]);
  const [duplicateAsk, setDuplicateAsk] = useState<{
    item: KbListItem;
    existing: KbDuplicateExisting | null;
    requestId: string;
    resolve: (confirm: boolean) => void;
  } | null>(null);

  const items = useQuery({
    queryKey: ["kbSectionItems", source.id, "all"],
    queryFn: ({ signal }) => fetchKbSectionItemsAll(source.id, signal),
    staleTime: 10_000,
  });

  const target = useQuery({
    queryKey: ["kbSection", targetId],
    queryFn: ({ signal }) => fetchKbSection(targetId, signal),
    enabled: targetId !== "",
    staleTime: 30_000,
  });

  const firstSelected = useMemo(
    () => (items.data?.items ?? []).find((i) => selected.has(i.itemId)) ?? null,
    [items.data?.items, selected],
  );

  const preview = useQuery({
    queryKey: ["kbMovePreview", firstSelected?.itemId ?? "", targetId],
    queryFn: () => fetchKbMovePreview(firstSelected!.itemId, targetId),
    enabled: step === "confirm" && firstSelected !== null && targetId !== "",
    staleTime: 0,
  });

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const moveOne = async (item: KbListItem): Promise<string> => {
    const requestId = newKbRequestId();
    const attempt = async (confirmDuplicateTitle: boolean): Promise<string> => {
      try {
        await moveKbItem(item.itemId, {
          targetSectionId: targetId,
          targetFolderId: targetFolderId || null,
          requestId,
          confirmDuplicateTitle,
        });
        return `✓ ${item.title || item.draftTitle || item.itemId}`;
      } catch (e) {
        if (
          !confirmDuplicateTitle &&
          e instanceof ApiClientError &&
          e.status === 409 &&
          (e.code === "duplicate_title" || JSON.stringify(e.details ?? {}).includes("duplicate_title"))
        ) {
          const details = (e.details ?? {}) as { existing?: KbDuplicateExisting };
          const confirmed = await new Promise<boolean>((resolve) =>
            setDuplicateAsk({ item, existing: details.existing ?? null, requestId, resolve }),
          );
          setDuplicateAsk(null);
          if (confirmed) return attempt(true);
          return `— пропущено (дублікат назви): ${item.title || item.draftTitle}`;
        }
        return `✗ ${item.title || item.draftTitle}: ${e instanceof Error ? e.message : "помилка"}`;
      }
    };
    return attempt(false);
  };

  const run = async () => {
    setStep("running");
    setLog([]);
    const list = (items.data?.items ?? []).filter((i) => selected.has(i.itemId));
    for (const item of list) {
      const line = await moveOne(item);
      setLog((prev) => [...prev, line]);
    }
    setStep("done");
    void qc.invalidateQueries({ queryKey: ["kbSectionItems"] });
    void qc.invalidateQueries({ queryKey: ["kbAdminSections"] });
    void qc.invalidateQueries({ queryKey: ["kbHome"] });
  };

  const rows = items.data?.items ?? [];

  return (
    <>
      <div
        className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4"
        role="dialog"
        aria-modal="true"
        aria-label="Перемістити матеріали"
      >
        <div className="flex max-h-[88vh] w-full max-w-xl flex-col rounded-xl border bg-card shadow-2xl">
          <div className="flex items-center justify-between border-b px-5 py-3.5">
            <span className="text-sm font-bold">Перемістити матеріали — {source.name}</span>
            <button
              type="button"
              onClick={onClose}
              aria-label="Закрити"
              className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-5 py-4">
            {step === "pick" && (
              <>
                <div className="mb-2 text-xs font-semibold">Матеріали ({selected.size} обрано)</div>
                <div className="mb-4 max-h-56 overflow-y-auto rounded-lg border">
                  {rows.length === 0 && (
                    <p className="px-3 py-4 text-sm text-muted-foreground">Матеріалів немає.</p>
                  )}
                  {rows.map((i) => (
                    <label
                      key={i.itemId}
                      className="flex cursor-pointer items-center gap-2 border-b px-3 py-2 text-sm last:border-b-0 hover:bg-muted/50"
                    >
                      <input
                        type="checkbox"
                        checked={selected.has(i.itemId)}
                        onChange={() => toggle(i.itemId)}
                        className="h-3.5 w-3.5 accent-primary"
                      />
                      <span className="min-w-0 flex-1 truncate">
                        {i.title || i.draftTitle}
                        {i.status && i.status !== "published" && (
                          <span className="ml-1.5 text-xs text-muted-foreground">({i.status === "draft" ? "чернетка" : "архів"})</span>
                        )}
                      </span>
                    </label>
                  ))}
                </div>

                <div className="mb-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-xs font-semibold">Цільовий розділ</label>
                    <select
                      value={targetId}
                      onChange={(e) => {
                        setTargetId(e.target.value);
                        setTargetFolderId("");
                      }}
                      className="w-full rounded-lg border bg-background px-3 py-2 text-sm outline-none"
                    >
                      <option value="">— оберіть —</option>
                      {candidates.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-semibold">Папка (у цільовому)</label>
                    <select
                      value={targetFolderId}
                      disabled={targetId === ""}
                      onChange={(e) => setTargetFolderId(e.target.value)}
                      className="w-full rounded-lg border bg-background px-3 py-2 text-sm outline-none disabled:opacity-50"
                    >
                      <option value="">— без папки —</option>
                      {(target.data?.folders ?? []).map((f) => (
                        <option key={f.id} value={f.id}>
                          {f.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </>
            )}

            {step === "confirm" && (
              <div className="text-sm">
                <p className="mb-3 font-semibold">
                  Перемістити {selected.size} матеріалів → «{target.data?.name ?? ""}»?
                </p>
                {preview.isPending ? (
                  <p className="text-muted-foreground">Рахуємо зміну доступу…</p>
                ) : preview.data ? (
                  <div className="space-y-2 rounded-lg border px-3.5 py-3 text-sm">
                    <p>
                      Побачать читачі <b>«{preview.data.to.name}»</b>
                      {preview.data.to.visibilityMode === "teams"
                        ? ` (команди: ${preview.data.to.teams.join(", ") || "жодної — DENY читачам"})`
                        : " (усі співробітники)"}
                      , перестануть бачити читачі <b>«{preview.data.from.name}»</b>
                      {preview.data.from.visibilityMode === "teams"
                        ? ` (команди: ${preview.data.from.teams.join(", ") || "жодної"})`
                        : " (усі співробітники)"}
                      .
                    </p>
                    {preview.data.pinCleared && (
                      <p className="text-xs text-muted-foreground">Закріплення буде знято (pin очищується при переносі).</p>
                    )}
                    <p className="text-xs text-muted-foreground">Обидві версії (чернетка й опублікована) переїжджають разом; аудит пишеться.</p>
                  </div>
                ) : (
                  <p className="text-destructive">Не вдалося порахувати зміну доступу.</p>
                )}
              </div>
            )}

            {(step === "running" || step === "done") && (
              <div className="text-sm">
                <p className="mb-2 font-semibold">
                  {step === "running" ? "Переміщуємо…" : "Готово."}
                </p>
                <ul className="space-y-1 rounded-lg border px-3.5 py-3 text-sm">
                  {log.map((l, i) => (
                    <li key={i}>{l}</li>
                  ))}
                </ul>
              </div>
            )}
            {error && (
              <p role="alert" className="mt-3 text-xs text-destructive">
                {error}
              </p>
            )}
          </div>

          <div className="flex justify-end gap-2 border-t px-5 py-3.5">
            {step === "pick" && (
              <>
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-md px-3.5 py-2 text-sm font-medium text-muted-foreground hover:bg-muted"
                >
                  Скасувати
                </button>
                <button
                  type="button"
                  disabled={selected.size === 0 || targetId === ""}
                  onClick={() => {
                    setError(null);
                    setStep("confirm");
                  }}
                  className="rounded-md bg-primary px-3.5 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50"
                >
                  Далі
                </button>
              </>
            )}
            {step === "confirm" && (
              <>
                <button
                  type="button"
                  onClick={() => setStep("pick")}
                  className="rounded-md px-3.5 py-2 text-sm font-medium text-muted-foreground hover:bg-muted"
                >
                  Назад
                </button>
                <button
                  type="button"
                  disabled={preview.isPending}
                  onClick={() => void run()}
                  className="rounded-md bg-primary px-3.5 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50"
                >
                  Перемістити
                </button>
              </>
            )}
            {step === "done" && (
              <button
                type="button"
                onClick={onClose}
                className="rounded-md bg-primary px-3.5 py-2 text-sm font-semibold text-primary-foreground"
              >
                Закрити
              </button>
            )}
          </div>
        </div>
      </div>

      {duplicateAsk && (
        <KbDuplicateDialog
          kind="title"
          existing={duplicateAsk.existing?.itemId ? duplicateAsk.existing : null}
          confirmLabel="Перемістити як окремий"
          onCancel={() => duplicateAsk.resolve(false)}
          onConfirm={() => duplicateAsk.resolve(true)}
        />
      )}
    </>
  );
}
